import React, { useState } from 'react'
import classNames from 'classnames'
import { type PredefinedCrossClientQuery } from '../../../../models/CrossClient'
import { onActivateKeyDown } from '../../utils'
import './CrossClientPredefinedQueryItem.scss'

export interface CrossClientPredefinedQueryItemProps {
  predefinedQuery: PredefinedCrossClientQuery
  bodyText: string
  layout: 'card' | 'row'
  onClick: (predefinedQuery: PredefinedCrossClientQuery) => void
  initLoading?: boolean
  runDisabled?: boolean
}

export const CrossClientPredefinedQueryItem: React.FC<CrossClientPredefinedQueryItemProps> = ({
  predefinedQuery,
  bodyText,
  layout,
  onClick,
  initLoading,
  runDisabled,
}) => {
  const isDisabled = runDisabled || initLoading
  const [hovered, setHovered] = useState<boolean>(false)

  const category = predefinedQuery.category
  const { textColor, iconBackgroundColor, iconBorderColor, icon } = category.metadata
  const hasBootstrapBaseClass = icon.value.split(' ').includes('bi')
  const iconClassName = hasBootstrapBaseClass ? icon.value : `bi ${icon.value}`

  const iconChip = (
    <div
      className='d-flex align-items-center justify-content-center cross-client-predefined-card-icon'
      style={{ backgroundColor: iconBackgroundColor, borderColor: iconBorderColor }}
    >
      {icon.type === 'svgUrl' ? (
        <img src={icon.value} alt='' aria-hidden='true' width='16' height='16' loading='lazy' referrerPolicy='no-referrer' />
      ) : (
        <i className={iconClassName} style={{ color: textColor }} aria-hidden='true' />
      )}
    </div>
  )

  if (layout === 'row') {
    return (
      <button
        type='button'
        className={classNames(
          'w-100 d-flex align-items-center zocks-gap-0.75 text-start py-2 zocks-px-0.75 zocks-border-0.75 border cross-client-predefined-row-item',
          { 'opacity-50': isDisabled },
        )}
        disabled={isDisabled}
        onClick={() => onClick(predefinedQuery)}
      >
        {iconChip}
        <div className='d-flex flex-column flex-fill mw-0 text-break'>
          <span className='paragraph-small bold' style={{ color: textColor }}>
            {predefinedQuery.title}
          </span>
          <span className='paragraph-medium zocks-text-gray-900'>{bodyText}</span>
        </div>
      </button>
    )
  }

  return (
    <div
      className={classNames(
        'd-flex flex-column align-items-start gap-2 p-3 zocks-border-0.75 border bg-white h-100 cross-client-transition cross-client-predefined-card',
        {
          'zocks-border-color-gray-200': !hovered || isDisabled,
          'zocks-border-color-primary cross-client-predefined-card-box-shadow': hovered && !isDisabled,
          'opacity-50 cross-client-predefined-card-disabled': isDisabled,
        },
      )}
      role={isDisabled ? undefined : 'button'}
      tabIndex={isDisabled ? undefined : 0}
      onClick={() => {
        if (isDisabled) {
          return
        }
        onClick(predefinedQuery)
      }}
      onKeyDown={isDisabled ? undefined : onActivateKeyDown(() => onClick(predefinedQuery))}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className='d-flex gap-2 align-items-center'>
        {iconChip}
        <div className='paragraph-small medium' style={{ color: textColor }}>
          {predefinedQuery.title}
        </div>
      </div>
      <div className='paragraph-small zocks-text-gray-900'>{bodyText}</div>
    </div>
  )
}
